import React from 'react';
import useMetadataStore from '@store/useMetadataStore';
import useGameStore from '@store/useGameStore';
import { getEffectIcon } from '@utils/statIcons';
import HoverClickPopup from '@components/ui/general/HoverClickPopup';
import UnitIcon from '@components/game/objects/UnitIcon';
import './EffectIcon.css';

/**
 * EffectIcon Component
 * Displays effect icons with two variants:
 * - 'icon' (default): Just the icon for EffectBar with hover/click popup
 * - 'with-name': Icon + name for Stats popup
 * 
 * @param {number} effectId - The effect ID from database
 * @param {object} activeEffect - The active effect instance with runtime data
 * @param {string} variant - 'icon' or 'with-name'
 * @param {string} className - Additional CSS classes
 */
const EffectIcon = ({ effectId, activeEffect = null, variant = 'icon', className = '' }) => {
  // Get effect data from metadata store
  const effect = useMetadataStore.getState().getEffectById(effectId);
  
  if (!effect) {
    //console.warn('[EffectIcon] Effect not found for ID:', effectId);
    return null;
  }
  
  // Get icon from animation_data or use fallback
  const iconData = effect.animation_data?.icon || {};
  const iconDisplay = getEffectIconDisplay(iconData, effect.slug);
  
  // Icon only variant (for EffectBar) - with popup if activeEffect provided
  if (variant === 'icon') {
    const iconElement = (
      <div
        className={`effect-icon ${className}`}
      >
        {iconDisplay}
      </div>
    );

    // If no activeEffect, just show icon without popup
    if (!activeEffect) {
      return iconElement;
    }

    // Get source unit for the effect
    const sourceUnit = activeEffect.source?.type === 'unit' && activeEffect.source?.id
      ? useGameStore.getState().getUnitOrAvailableUnitById(activeEffect.source.id)
      : null;
    
    // Render with popup
    return (
      <HoverClickPopup
        trigger={iconElement}
        className="effect-popup-container"
      >
        <div className="effect-popup">
          {/* Header with icon and name */}
          <div className="effect-popup-header">
            <span className="effect-popup-icon">{iconDisplay}</span>
            <span className="effect-popup-title">{effect.name}</span>
          </div>
          
          {/* Description */}
          <div className="effect-popup-description">
            {effect.description || 'No description available'}
          </div>
          
          {/* Separator */}
          <div className="effect-popup-separator" />
          
          {/* Details */}
          <div className="effect-popup-details">
            {/* Duration */}
            <div className="effect-detail-row">
              <span className="effect-detail-label">Duration:</span>
              <span className="effect-detail-value">
                {(() => {
                  if (activeEffect.is_permanent) {
                    // Check if this is a counter effect
                    if (activeEffect.counter_state) {
                      const current = activeEffect.counter_state.current_turn_triggers || 0;
                      const max = activeEffect.counter_state.max_per_turn || 0;
                      return `Permanent (${current}/${max} this turn)`;
                    }
                    return 'Permanent';
                  }

                  // FAIL LOUDLY if missing data
                  if (activeEffect.turns_remaining === null || activeEffect.turns_remaining === undefined) {
                    console.error('[EffectIcon] Missing turns_remaining:', activeEffect);
                    throw new Error(`Effect ${activeEffect.id} missing turns_remaining`);
                  }

                  if (!activeEffect.max_turns) {
                    console.error('[EffectIcon] Missing max_turns:', activeEffect);
                    throw new Error(`Effect ${activeEffect.id} missing max_turns`);
                  }

                  return `${activeEffect.turns_remaining}/${activeEffect.max_turns} turns`;
                })()}
              </span>
            </div>

            {/* Counter Usage - only show for counter effects */}
            {activeEffect.counter_state && (
              <div className="effect-detail-row">
                <span className="effect-detail-label">Counter Usage:</span>
                <span className="effect-detail-value">
                  {(() => {
                    const current = activeEffect.counter_state.current_turn_triggers || 0;
                    const max = activeEffect.counter_state.max_per_turn || 0;
                    const remaining = Math.max(0, max - current);

                    if (current === 0) {
                      return <span className="counter-ready">Ready ({max} available)</span>;
                    } else if (remaining > 0) {
                      return <span className="counter-partial">{remaining} remaining (used {current})</span>;
                    } else {
                      return <span className="counter-depleted">Depleted ({current}/{max})</span>;
                    }
                  })()}
                </span>
              </div>
            )}

            {/* Source */}
            <div className="effect-detail-row">
              <span className="effect-detail-label">Source:</span>
              <span className="effect-detail-value source">
                {sourceUnit ? (
                  <>
                    <UnitIcon unitId={sourceUnit.id} size="tiny" />
                    <span>{sourceUnit.name}</span>
                  </>
                ) : (
                  activeEffect.source?.name || 'Unknown'
                )}
              </span>
            </div>
          </div>
        </div>
      </HoverClickPopup>
    );
  }
  
  // Icon with name variant (for Stats popup)
  if (variant === 'with-name') {
    return (
      <div className={`effect-icon-with-name ${className}`}>
        <span className="effect-icon-display">
          {iconDisplay}
        </span>
        <span className="effect-name">
          {effect.name}
        </span>
      </div>
    );
  }
  
  console.warn('[EffectIcon] Unknown variant:', variant);
  return null;
};

/**
 * Helper function to get the icon display
 * Uses animation_data from backend or falls back to statIcons
 */
const getEffectIconDisplay = (iconData, slug) => {
  // Check if we have icon data from backend
  if (iconData?.value) {
    switch (iconData.type) {
      case 'unicode':
        return <span className="unicode-icon">{iconData.value}</span>;
      
      case 'image':
        return <img src={iconData.value} alt={iconData.fallback || slug} />;
      
      case 'css-class':
        return <i className={iconData.value}></i>;
      
      default:
        console.warn('[EffectIcon] Unknown icon type:', iconData.type);
    }
  }
  
  // Fallback to statIcons utility
  const fallbackIcon = getEffectIcon(slug);
  if (fallbackIcon) {
    return fallbackIcon;
  }
  
  // Last resort fallback
  return <span className="fallback-icon">?</span>;
};

export default EffectIcon;